import { useFullRelease } from "@/common/hooks_useFullRelease";
import Select from "@imminently/decisively-ui/Select";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDerivedTableDataSource } from "./DerivedTableDataSourceContext";
import { useDerivedTableDebug } from "./DerivedTableDebugContext";

/**
 * Table/instance pickers, shared between the live debugger and the Session Report's stored view -
 * see DerivedTableDataSourceContext for how the two differ. This component only owns the
 * selection UI; it reads instance options from whichever data source is currently in scope.
 */
const DerivedTableToolbarControls = () => {
  const { t } = useTranslation();
  const release = useFullRelease();
  const { selectedTableId, setSelectedTableId, selectedInstance, setSelectedInstance } = useDerivedTableDebug();
  const { instanceOptions } = useDerivedTableDataSource();

  const derivedTables = release?.derivedTables || [];
  const tableOptions = React.useMemo(
    () => derivedTables.map((table) => ({ value: table.id, label: table.name })),
    [derivedTables],
  );

  React.useEffect(() => {
    if (!selectedTableId && derivedTables.length > 0) {
      setSelectedTableId(derivedTables[0].id);
    }
  }, [selectedTableId, derivedTables, setSelectedTableId]);

  React.useEffect(() => {
    if (instanceOptions.length === 0) {
      if (selectedInstance) setSelectedInstance(null);
      return;
    }
    if (!selectedInstance || !instanceOptions.some((option) => option.value === selectedInstance)) {
      setSelectedInstance(instanceOptions[0].value);
    }
  }, [instanceOptions, selectedInstance, setSelectedInstance]);

  return (
    <>
      <Select
        value={selectedTableId}
        options={tableOptions}
        onValueChange={setSelectedTableId}
        placeholder={t("debugger.derived_table_select_table")}
        className="min-w-[10rem]"
      />
      {instanceOptions.length > 1 ? (
        <Select
          value={selectedInstance}
          options={instanceOptions}
          onValueChange={setSelectedInstance}
          placeholder={t("debugger.derived_table_select_instance")}
          className="min-w-[10rem]"
        />
      ) : null}
    </>
  );
};

export default DerivedTableToolbarControls;
